import React from 'react';

import { Anthropic } from '../../icons/Anthropic';
import { Azure } from '../../icons/Azure';
import { DeepSeek } from '../../icons/DeepSeek';
import { Gemini } from '../../icons/Gemini';
import { OpenAI } from '../../icons/OpenAI';
import { ProviderName } from '../../types';

export const MAX_MODEL_NAME = 27; // 27 + 5 characters for the random id = 32

const GPT5_EFFORT_OPTIONS = ['minimal', 'low', 'medium', 'high'] as const;
const GPT5_NANO_EFFORT_OPTIONS = ['low', 'medium', 'high'] as const;
const GPT51_PLUS_EFFORT_OPTIONS = ['none', 'low', 'medium', 'high'] as const;

export const isGpt5Model = (model: string) => model?.startsWith('gpt-5');

const isGpt5NanoModel = (model: string) => /^gpt-5-nano/i.test(model);

const isGpt51PlusModel = (model: string) => {
  const match = model?.match(/^gpt-5\.(\d+)/);
  return match !== null && parseInt(match[1], 10) >= 1;
};

export const getDefaultReasoningEffort = (model: string) => {
  if (isGpt51PlusModel(model)) {
    return 'none';
  }
  if (isGpt5NanoModel(model)) {
    return 'low';
  }
  if (isGpt5Model(model)) {
    return 'medium';
  }
  return '';
};

export const getReasoningEffortOptions = (model: string) => {
  if (isGpt51PlusModel(model)) {
    return GPT51_PLUS_EFFORT_OPTIONS;
  }
  if (isGpt5NanoModel(model)) {
    return GPT5_NANO_EFFORT_OPTIONS;
  }
  return GPT5_EFFORT_OPTIONS;
};

const ICON_SIZE_CLASSES: Record<number, string> = { 16: 'size-4', 24: 'size-6', 32: 'size-8' };

// Returns undefined for providers without a dedicated logo (e.g. `moveo`/`moveo-ai`);
// the Moveo brand mark is rendered by callers, not here.
export const modelIconResolver = (
  provider: ProviderName,
  size = 24
): React.JSX.Element | undefined => {
  // The size class prevents parent selectors like
  // `[&_svg:not([class*='size-'])]:size-4` from overriding the icon size.
  const className = ICON_SIZE_CLASSES[size] ?? 'size-6';
  switch (provider) {
    // OpenAI and Anthropic's logos are solid black with no dark-mode variant, so they
    // disappear against a dark background without a light backdrop behind them.
    case 'openai':
      return (
        <span className="inline-flex shrink-0 items-center justify-center rounded-full bg-white p-0.5">
          <OpenAI size={size} className={className} />
        </span>
      );
    case 'anthropic':
      return (
        <span className="inline-flex shrink-0 items-center justify-center rounded-full bg-white p-0.5">
          <Anthropic size={size} className={className} />
        </span>
      );
    case 'azure':
      return <Azure size={size} className={className} />;
    case 'gemini':
    case 'vertex_ai':
      // Vertex AI serves Gemini models — reuse Google's Gemini logo.
      return <Gemini size={size} className={className} />;
    case 'deepseek':
      return <DeepSeek size={size} className={className} />;
  }
};

// English fallback for providers we don't recognize. Callers that render to the
// user (e.g. ModelPicker) translate via the `provider_unknown` i18n key instead.
export const UNKNOWN_PROVIDER_LABEL = 'Unknown';

export const providerNameMapper = (provider: ProviderName): string => {
  switch (provider) {
    case 'openai':
      return 'OpenAI';
    case 'anthropic':
      return 'Anthropic';
    case 'azure':
      return 'Azure';
    case 'gemini':
      return 'Gemini';
    case 'vertex_ai':
      return 'Vertex AI';
    case 'moveo':
    case 'moveo-ai':
      return 'Moveo.AI';
    case 'deepseek':
      return 'DeepSeek';
    default:
      return UNKNOWN_PROVIDER_LABEL;
  }
};
